import Joi from 'joi';
import { ProductType } from 'constants.js';
import * as scheduledConstants from 'views/Campaign/components/Recipients/components/constants';
import { queryParamsValidator } from './queryParams';

const recurrenceExpression = Joi.string()
  .required()
  .label('Recurrence detail')
  .messages({ 'string.base': '"Recurrence detail" is required' });

const scheduleActionDate = Joi.date()
  .iso()
  .required()
  .label('Schedule DateTime')
  .messages({
    'any.invalid': '"Schedule DateTime" is invalid',
    'any.required': '"Schedule DateTime" is required'
  });

const delayDeliveryTime = Joi.string()
  .pattern(/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/)
  .label('Delay Delivery Time')
  .messages({
    'any.invalid': '"Delay Delivery Time" is invalid',
    'any.required': '"Delay Delivery Time" is required',
    'string.pattern.base': '"Delay Delivery Time" is invalid',
    'string.base': '"Delay Delivery Time" is invalid'
  });

const scheduleNow = Joi.bool()
  .required()
  .label('ScheduleNow')
  .messages({
    'any.invalid': '"ScheduleNow" is invalid'
  });

const scheduleUnit = Joi.string()
  .required()
  .label('Units of time')
  .messages({ 'string.base': '"Units of time" is required' });

const numberOfUnits = Joi.number()
  .integer()
  .positive()
  .required()
  .label('Number of time units');

const scheduleRule = Joi.object({})
  .when('campaignOccurrenceType', {
    is: 'SCHEDULED',
    then: Joi.object({})
      .when(`scheduleRule.${scheduledConstants.UNIT_TIME_BEFORE_ARRIVAL}`, {
        is: Joi.exist(),
        then: Joi.object({
          [scheduledConstants.UNIT_TIME_BEFORE_ARRIVAL]: numberOfUnits,
          scheduleUnit
        })
      })
      .required()
      .messages({ 'any.required': `'Schedule campaign' is required` })
      .when(
        Joi.ref(`scheduleRule.${scheduledConstants.UNIT_TIME_AFTER_CHECKOUT}`),
        {
          is: Joi.exist(),
          then: Joi.object({
            [scheduledConstants.UNIT_TIME_AFTER_CHECKOUT]: numberOfUnits,
            scheduleUnit
          })
        }
      )
      .required()
      .messages({ 'any.required': `'Schedule campaign' is required` })
      .when(
        Joi.ref(`scheduleRule.${scheduledConstants.UNIT_TIME_AFTER_ARRIVAL}`),
        {
          is: Joi.exist(),
          then: Joi.object({
            [scheduledConstants.UNIT_TIME_AFTER_ARRIVAL]: numberOfUnits,
            scheduleUnit
          })
        }
      )
      .required()
      .messages({ 'any.required': `'Schedule campaign' is required` })
      .when(
        Joi.ref(`scheduleRule.${scheduledConstants.UNIT_TIME_BEFORE_CHECKOUT}`),
        {
          is: Joi.exist(),
          then: Joi.object({
            [scheduledConstants.UNIT_TIME_BEFORE_CHECKOUT]: numberOfUnits,
            scheduleUnit
          })
        }
      )
      .required()
      .messages({ 'any.required': `'Schedule campaign' is required` })
      .when(
        Joi.ref(`scheduleRule.${scheduledConstants.UNIT_TIME_AFTER_CONFIRMATION}`),
        {
          is: Joi.exist(),
          then: Joi.object({
            [scheduledConstants.UNIT_TIME_AFTER_CONFIRMATION]: numberOfUnits,
            scheduleUnit
          })
        }
      )
      .required()
      .messages({ 'any.required': `'Schedule campaign' is required` })
  })
  .when('campaignOccurrenceType', {
    is: 'ON_DEMAND',
    then: Joi.object({})
      .when(Joi.ref(`recurrence`), {
        is: true,
        then: Joi.object({
          recurrenceExpression
        }),
        otherwise: Joi.object({}).when(Joi.ref(`scheduleRule.scheduleNow`), {
          is: true,
          then: Joi.object({
            scheduleNow
          }),
          otherwise: Joi.object({
            scheduleActionDate
          })
        })
      })
      .required()
      .messages({ 'any.required': `'Schedule' is required` })
  })
  .when('campaignOccurrenceType', {
    is: 'ON_EVENT',
    then: Joi.object({})
      .when(Joi.ref(`scheduleRule.delayUntilNextDay`), {
        is: true,
        then: Joi.object({
          delayDeliveryTime: delayDeliveryTime.required()
        }),
        otherwise: Joi.object({
          delayDeliveryTime
        })
      })
  });

const campaignTemplatesValidator = Joi.object({
  communicationTemplateUuid: Joi.string()
    .label('Template')
    .required(),
  valid: Joi.bool().valid(true),
  subConditionExpressions: Joi.array()
    .min(1)
    .message('Rule based templates must have at least 1 rule')
});
const campaignExtraAddressesValidator = Joi.string().email({
  minDomainSegments: 2,
  tlds: { allow: false }
});

const emailCampaignData = Joi.object({})
  .when('type', {
    is: 'EMAIL',
    then: Joi.object({
      fromName: Joi.string()
        .label('From Name')
        .min(3)
        .required(),
      fromAddress: Joi.string()
        .label('From Address')
        .email({ minDomainSegments: 2, tlds: { allow: false } })
        .required(),
      replyAddress: Joi.string()
        .label('Reply Address')
        .required()
        .email({ minDomainSegments: 2, tlds: { allow: false } }),
      ccAddresses: Joi.array().items(campaignExtraAddressesValidator),
      bccAddresses: Joi.array().items(campaignExtraAddressesValidator)
    })
  })

  .when('type', {
    is: 'SMS',
    then: Joi.object({
      fromName: Joi.string()
        .label('From Name')
        .allow(''),
      emailSubject: Joi.string()
        .label('Email Subject')
        .allow(''),
      fromAddress: Joi.string()
        .label('From Address')
        .allow(''),
      replyAddress: Joi.string()
        .label('Reply Address')
        .allow('')
    })
  });

const campaignValidator = Joi.object({
  name: Joi.string()
    .label('Campaign Name')
    .min(3)
    .max(100)
    .required(),
  productName: Joi.string()
    .label('Product Type')
    .required(),
  subscriberListId: Joi.when('campaignOccurrenceType', {
    is: 'ON_DEMAND' || 'INTERNAL_SURVEY_RESPONSE',
    then: Joi.when('productType', {
      is: ProductType.List,
      then: Joi.string()
        .label('Subscribers List')
        .required()
    })
  }),
  schemaIdentifier: Joi.string()
    .label('Event Type')
    .required(),
  templates: Joi.array().items(campaignTemplatesValidator),
  campaignExclusionConditionExpression: Joi.object({
    valid: Joi.bool().valid(true),
    subConditionExpressions: Joi.array()
  }),
  campaignOccurrenceType: Joi.string()
    .label('Campaign Type')
    .required(),
  type: Joi.string()
    .label('Campaign Type:')
    .required(),
  startDate: Joi.date()
    .label('Start Date')
    .required(),
  endDate: Joi.date()
    .label('End Date')
    .min(Joi.ref('startDate'))
    .message('End Date must be same or after Start Date')
    .required(),
  active: Joi.bool(),
  backport: Joi.when('campaignOccurrenceType', {
    is: 'ON_EVENT',
    then: Joi.bool().invalid(true),

    otherwise: Joi.bool()
  })
    .label('Apply to Existing Reservations')
    .messages({
      'any.invalid':
        'Setting may only be used for scheduled campaigns. Please change the Campaign Type under Campaign Automation'
    }),
  scheduleRule,
  communicationTemplateUuid: Joi.string()
    .label('Template')
    .when('templates', {
      is: Joi.exist(),
      then: Joi.allow(''),
      otherwise: Joi.required()
    }),
  campaignRecipientQueryUuid: Joi.when('campaignOccurrenceType', {
    is: 'ON_DEMAND',
    then: Joi.string().required()
  }).label('Campaign Recipient Query'),
  tenantId: Joi.string(),
  businessContextId: Joi.string(),
  createTime: Joi.date(),
  updateTime: Joi.date(),
  id: Joi.string(),
  emailCampaignData: emailCampaignData,
  toAddresses: Joi.when('campaignOccurrenceType', {
    is: 'INTERNAL_SURVEY_RESPONSE',
    then: Joi.when('type', {
      is: 'EMAIL',
      then: Joi.array()
        .required()
        .min(1)
        .items(campaignExtraAddressesValidator)
    }).when('type', {
      is: 'SMS',
      then: Joi.array()
        .required()
        .min(1)
        .items(Joi.number())
    })
  }).label('To Addresses'),
  attachmentTemplateUuids: Joi.array().items(Joi.string().label('Attachment'))
});

export default campaignValidator;
